/**
 * ModePicker — a small, controlled select for choosing a queue mode
 * ("1v1" vs "3v3") before quick-joining. Mirrors `ChampionPicker`'s shape:
 * a labeled `<select>`, no ranked toggle (ranked stays `false` always —
 * see #443 for the not-yet-built ranked/MMR architecture).
 */

import type { ReactElement } from "react";
import { toModeId, type ModeId } from "@pit/protocol";

/** Modes offered by the picker. Must match `MODE_TABLE` on the server
 * (`apps/server/src/modes.ts`) — this list is display-only; the server is
 * still the source of truth and rejects anything it doesn't recognize. */
const MODES: ReadonlyArray<{ readonly id: ModeId; readonly label: string }> = [
  { id: toModeId("1v1"), label: "1v1" },
  { id: toModeId("3v3"), label: "3v3" },
];

export interface ModePickerProps {
  readonly value: ModeId;
  readonly onChange: (id: ModeId) => void;
  readonly disabled?: boolean;
}

export function ModePicker({ value, onChange, disabled = false }: ModePickerProps): ReactElement {
  return (
    <label
      data-testid="mode-picker"
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}
    >
      <span style={{ opacity: 0.75 }}>Mode</span>
      <select
        data-testid="mode-select"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(toModeId(e.target.value));
        }}
        style={{
          padding: "6px 10px",
          fontSize: 14,
          borderRadius: 4,
          border: "1px solid #3a4757",
          background: "#161c24",
          color: "#dfe6ef",
        }}
      >
        {MODES.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
    </label>
  );
}
